// @ts-nocheck
import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { isCompensationDataCompleteForIndividuals } from '../../../../utils/compensationStep';
import {
  PageHeader,
  Card,
  Button,
  InfoBanner,
  Th,
  Td,
  WorkflowDateField,
} from '../../components/WorkflowFormPrimitives';
import { fmt, formatWithCommas, cleanCurrencyInput } from '../../utils/formatCurrency';
import { useCompanyContext } from '../../context/CompanyContext';

export const CompensationForm = ({ formData, setFormData, setActiveTab, continueMode }) => {
  const { isCurrentAnalysisLocked } = useCompanyContext();
  const individuals = formData.individuals || [];
  const uniqueNames = Array.from(new Set(individuals.map((i: { name: string }) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const data = formData.compensation || {};
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const update = (id, k, v) =>
    setFormData((p) => {
      const nextRow = { ...((p.compensation || {})[id] || {}), [k]: v };
      const nextComp = { ...(p.compensation || {}), [id]: nextRow };
      if (k === 'startDate') {
        return {
          ...p,
          compensation: nextComp,
          baseAmountData: {
            ...(p.baseAmountData || {}),
            [id]: { ...((p.baseAmountData || {})[id] || {}), startDate: v },
          },
        };
      }
      return { ...p, compensation: nextComp };
    });

  const canContinueCompensation =
    uniqueIndividuals.length > 0 && isCompensationDataCompleteForIndividuals(uniqueIndividuals, data);
  const forwardPrimaryLabel = isCurrentAnalysisLocked ? 'Continue' : 'Save and continue';

  return (
    <div className="space-y-5">
      <PageHeader title="Compensation" subtitle="Enter Start Date and Base Salary for Each Disqualified Individual" />
      {uniqueIndividuals.length === 0 ? (
        <InfoBanner variant="warning" showIcon>
          Please add disqualified individuals first.
        </InfoBanner>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th>Name</Th>
                  <Th>Start Date</Th>
                  <Th>Base Salary</Th>
                  <Th>Target Bonus (%)</Th>
                  <Th>Health Premium ($/mo)</Th>
                  <Th className="bg-emerald-50/50">Target Bonus $</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {uniqueIndividuals.map((ind) => {
                  const d = data[ind.id] || {};
                  const targetBonus = (+d.baseSalary || 0) * (+d.bonusPct || 0);
                  return (
                    <tr key={ind.id} className="hover:bg-gray-50/50">
                      <Td highlight>
                        <div>
                          <span className="text-gray-900">{ind.name}</span>
                          <br />
                          <span className="text-xs text-gray-400">{ind.title}</span>
                        </div>
                      </Td>
                      <Td>
                        <WorkflowDateField
                          compact
                          value={d.startDate}
                          onChange={(v) => update(ind.id, 'startDate', v)}
                        />
                      </Td>
                      <Td>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={
                            focusedField === `${ind.id}-baseSalary`
                              ? d.baseSalary || ''
                              : formatWithCommas(d.baseSalary || '')
                          }
                          onChange={(e) => update(ind.id, 'baseSalary', cleanCurrencyInput(e.target.value))}
                          onFocus={() => setFocusedField(`${ind.id}-baseSalary`)}
                          onBlur={() => setFocusedField(null)}
                          className="w-28 h-8 px-2 text-xs border border-gray-300 rounded-md"
                          placeholder="0"
                        />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-0.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={d.bonusPct !== '' && d.bonusPct != null ? String(Number(d.bonusPct) * 100) : ''}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/%/g, '').trim();
                              if (raw === '') {
                                update(ind.id, 'bonusPct', '');
                                return;
                              }
                              const num = parseFloat(raw);
                              if (!Number.isNaN(num)) update(ind.id, 'bonusPct', String(num / 100));
                            }}
                            className="w-16 h-8 px-2 text-xs border border-gray-300 rounded-md"
                            placeholder="20"
                          />
                          <span className="text-xs text-gray-500">%</span>
                        </div>
                      </Td>
                      <Td>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={
                            focusedField === `${ind.id}-healthPremium`
                              ? d.healthPremium || ''
                              : formatWithCommas(d.healthPremium || '')
                          }
                          onChange={(e) => update(ind.id, 'healthPremium', cleanCurrencyInput(e.target.value))}
                          onFocus={() => setFocusedField(`${ind.id}-healthPremium`)}
                          onBlur={() => setFocusedField(null)}
                          className="w-24 h-8 px-2 text-xs border border-gray-300 rounded-md"
                          placeholder="1,500"
                        />
                      </Td>
                      <Td className="bg-emerald-50/30 font-medium text-emerald-700">{fmt(targetBonus)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <InfoBanner showIcon>
        <strong>Note:</strong> <strong>Start date</strong> and <strong>base salary</strong> are required for everyone in
        the list before you can continue. Start date is shared with Base Amount — enter it once here (or once on Base
        Amount if you start there); the other step shows the same value automatically. Enter target bonus as a
        percentage (e.g., 20 for 20%).
      </InfoBanner>
      <div className="flex justify-between gap-3">
        <Button variant="secondary" onClick={() => setActiveTab('individuals')}>
          Previous
        </Button>
        {continueMode === 'dual' ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={!canContinueCompensation}
              onClick={() => setActiveTab('baseAmount', { persist: false })}
            >
              Continue <ChevronRight size={14} className="ml-1.5" />
            </Button>
          </div>
        ) : (
          <Button disabled={!canContinueCompensation} onClick={() => setActiveTab('baseAmount', { persist: true })}>
            {forwardPrimaryLabel} <ChevronRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
